import { BefehlFormAuftrag, BefehlFormField } from '../Generic'
import type { RadioChipOption } from '../../form'
import {
  BetriebsstelleField,
  BetriebsstellenartField,
  RadioChipGroup
} from '../../form'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Befehl21FormData } from './schema'
import { Betriebsstellenart } from '../../../types'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag21_10FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

function determineAufDerOrInFromBetriebsstellenart(
  sel21_11Betriebsstellenart: '' | Betriebsstellenart
) {
  if (
    sel21_11Betriebsstellenart === Betriebsstellenart.Abzw ||
    sel21_11Betriebsstellenart === Betriebsstellenart.Uest
  )
    return 'auf_der'
  else if (
    sel21_11Betriebsstellenart === Betriebsstellenart.Bft ||
    sel21_11Betriebsstellenart === Betriebsstellenart.Bf
  )
    return 'in'
  else return ''
}

export default function Auftrag21_10Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag21_10FormProps>) {
  const { t } = useTranslation('ui-befehle')

  const formMethods = useFormContext<Befehl21FormData>()
  const { control, formState, watch } = formMethods
  const { errors } = formState

  const sel21_11Betriebsstellenart = watch('auftrag21_10.feld21_11.art')

  return (
    <BefehlFormAuftrag
      auftragKennung="21.10"
      fieldName="auftrag21_10.enabled"
      readOnly={true}
      disabled={false}
      testId={testId}
    >
      <div className="@container">
        <div
          className="flex flex-wrap @[400px]:flex-nowrap items-baseline gap-2"
          data-testid={`${testId}-outer-div`}
        >
          <div className="flex flex-row items-baseline gap-1 flex-shrink-0">
            <span data-testid={`${testId}-text`}>
              {t('auftraege.21_10.darf')}
            </span>
            <RadioChipGroup
              value={determineAufDerOrInFromBetriebsstellenart(
                sel21_11Betriebsstellenart
              )}
              options={
                [
                  { value: 'in', label: t('auftraege.21_10.in') },
                  { value: 'auf_der', label: t('auftraege.21_10.auf_der') }
                ] as RadioChipOption[]
              }
              variant="plain"
              showOnlySelected={readOnly}
              testId={`${testId}-feld-21-11-preposition`}
            />
          </div>
          <BorderedBox
            className="w-full"
            footer={
              <span data-testid={`${testId}-feld-21-11-label`}>
                {t('auftraege.21_10.fields.feld_21_11_label')}
              </span>
            }
            data-testid={`${testId}-feld-21-11-bordered-box`}
          >
            <ZugmeldestelleWrapper
              betriebsstellenartField={
                <BefehlFormField
                  name="auftrag21_10.feld21_11.art"
                  control={control}
                  label="Betriebsstellenart"
                  readOnly={readOnly}
                  disabled={false}
                  activateEmptyWarning={activateEmptyFieldWarnings}
                  useTextDisplayInReadOnly={true}
                  data-testid={`${testId}-feld-21-11-art`}
                  render={({ field, isDisabled, isReadOnly }) => (
                    <BetriebsstellenartField
                      {...field}
                      label={t(
                        'auftraege.21_10.fields.betriebsstellenart_label'
                      )}
                      variant="solid"
                      errors={errors}
                      selectClassName="w-full"
                      disabled={isDisabled}
                      readOnly={isReadOnly}
                      data-testid={`${testId}-feld-21-11-art-inner`}
                    />
                  )}
                />
              }
              bestriebsstelleField={
                <BefehlFormField
                  name="auftrag21_10.feld21_11.name"
                  control={control}
                  label={t('auftraege.21_10.fields.zugmeldestelle')}
                  readOnly={readOnly}
                  disabled={false}
                  activateEmptyWarning={activateEmptyFieldWarnings}
                  useTextDisplayInReadOnly={true}
                  data-testid={`${testId}-feld-21-11-name`}
                  render={({ field, isDisabled, isReadOnly }) => (
                    <BetriebsstelleField
                      {...field}
                      label={t('auftraege.21_10.fields.zugmeldestelle')}
                      errors={errors}
                      disabled={isDisabled}
                      readOnly={isReadOnly}
                      typ={sel21_11Betriebsstellenart}
                      data-testid={`${testId}-feld-21-11-name-inner`}
                    />
                  )}
                />
              }
            />
          </BorderedBox>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
